import type { FunctionComponent } from 'react';
import { useDispatch } from 'react-redux';
import Route from '../../utils/Route';
import PersonaContext from '../persona/PersonaContext';
import PlayerScreen from './PlayerScreen.Container';
import IntroScreen from './IntroScreen.Container';
import WhyIntroScreen from './WhyIntroScreen';
import CouncilIntroScreen from './CouncilIntroScreen';
import PersonasIntroScreen from './PersonasIntroScreen';
import PersonaIntroScreen from './PersonaIntroScreen';
import ScoreIntroScreen from './ScoreIntroScreen';
import ActionsIntroScreen from './ActionsIntroScreen';
import ConceptsIntroScreen from './ConceptsIntroScreen.Container';
import ConceptIntroScreen from './ConceptIntroScreen';
import EffectIntroScreen from './EffectIntroScreen';
import ResultIntroScreen from './ResultIntroScreen';
import FeedbackIntroScreen from './FeedbackIntroScreen';
import SummaryIntroScreen from './SummaryIntroScreen';
import SeasonIntroScreen from './SeasonIntroScreen';
import {
	finishIntro,
	newPlaythrough,
	nextRound
} from '../../store/PlaythroughSlice';
import type { Persona } from '../../data/Personas';
import { useService } from '@xstate/react';
import interpreter from '../../state/service';
import DialogTransitionRoute from '../../utils/DialogTransitionRoute';

const ACTIVE_PERSONA_INDEX = 0;

export interface Props {}

const Intro: FunctionComponent<Props> = () => {
	const dispatch = useDispatch();
	const [state, send] = useService(interpreter);

	return (
		<>
			<DialogTransitionRoute key="player" state={state} stateId="intro.player">
				<PlayerScreen
					onNext={(playerName: string) => {
						dispatch(newPlaythrough({ playerName }));
						send('NEXT');
					}}
				/>
			</DialogTransitionRoute>
			<DialogTransitionRoute key="start" state={state} stateId="intro.start">
				<IntroScreen
					onDetails={() => send('DETAILS')}
					onNext={() => send('NEXT')}
					onClose={() => send('CLOSE')}
				/>
			</DialogTransitionRoute>
			<DialogTransitionRoute key="why" state={state} stateId="intro.why">
				<WhyIntroScreen
					onClose={() => send('CLOSE')}
					onNext={() => send('NEXT')}
				/>
			</DialogTransitionRoute>
			<Route key="council" state={state} stateId="intro.council">
				<CouncilIntroScreen onNext={() => send('NEXT')} />
			</Route>
			<Route key="personas" state={state} stateId="intro.personas">
				<PersonasIntroScreen activePersonaIndex={ACTIVE_PERSONA_INDEX} />
			</Route>
			<DialogTransitionRoute
				key="persona"
				state={state}
				stateId="intro.persona"
			>
				<PersonaContext
					personaName={state.context.personaName}
					render={(persona: Persona) => (
						<PersonaIntroScreen
							persona={persona}
							onClose={() => send('CLOSE')}
						/>
					)}
				/>
			</DialogTransitionRoute>
			<Route key="score" state={state} stateId="intro.score">
				<ScoreIntroScreen onNext={() => send('NEXT')} />
			</Route>
			<Route key="actions" state={state} stateId="intro.actions">
				<ActionsIntroScreen />
			</Route>
			<DialogTransitionRoute
				key="concepts"
				state={state}
				stateId="intro.concepts"
			>
				<ConceptsIntroScreen
					onClose={() => send('CLOSE')}
					onConcept={() => send('DETAILS')}
					onActivate={() => send('ACTIVATE')}
				/>
			</DialogTransitionRoute>
			<DialogTransitionRoute
				key="concept"
				state={state}
				stateId="intro.concept"
			>
				<ConceptIntroScreen
					onClose={() => send('CLOSE')}
					onActivate={() => send('ACTIVATE')}
				/>
			</DialogTransitionRoute>
			<DialogTransitionRoute key="effect" state={state} stateId="intro.effect">
				<EffectIntroScreen
					onNext={() => {
						send('NEXT');
					}}
				/>
			</DialogTransitionRoute>
			<DialogTransitionRoute key="result" state={state} stateId="intro.result">
				<ResultIntroScreen
					onNext={(concept) => {
						dispatch(nextRound({ concept }));
						send('NEXT');
					}}
				/>
			</DialogTransitionRoute>
			<Route key="feedback" state={state} stateId="intro.feedback">
				<FeedbackIntroScreen onNext={() => send('NEXT')} />
			</Route>
			<Route key="summary" state={state} stateId="intro.summary">
				<SummaryIntroScreen onNext={() => send('NEXT')} />
			</Route>
			<Route key="season" state={state} stateId="intro.season">
				<SeasonIntroScreen
					onNext={() => {
						dispatch(finishIntro());
						send('NEXT');
					}}
				/>
			</Route>
		</>
	);
};

export default Intro;
